import type {
	QuoteSummary,
	SalesPerformance,
	SalesPerformanceRow,
} from "~/features/ar-dashboard/schemas/dashboard-schemas.ts";

export const markQuoteMetricsUnavailable = (
	salesPerformance: SalesPerformance,
): SalesPerformance => ({
	...salesPerformance,
	rows: salesPerformance.rows.map((row) => ({
		...row,
		quotes: null,
		quoteRatio: null,
	})),
});

/**
 * Merges Quotes / SQ Ratio metrics sourced from the LTL Quoting API
 * `customers/quote-summary` endpoint into the Sales Performance data, which
 * supplies shipments and brokerage revenue from the Trax AR API.
 *
 * Quote metrics are matched to sales performance rows by `customerId`. Rows
 * without a matching quote summary receive zero quotes and a zero ratio.
 * Customers that only appear in the quote summary (quote activity but no
 * shipments) are appended so they still display.
 */
export const mergeQuoteSummary = (
	salesPerformance: SalesPerformance,
	quoteSummary: QuoteSummary,
): SalesPerformance => {
	const summaryByCustomerId = new Map(
		quoteSummary.map((row) => [row.customerId, row]),
	);

	const seenCustomerIds = new Set<number>();
	const mergedRows: SalesPerformanceRow[] = salesPerformance.rows.map((row) => {
		seenCustomerIds.add(row.customerId);
		const summary = summaryByCustomerId.get(row.customerId);
		return {
			...row,
			quotes: summary?.quotes ?? 0,
			quoteRatio: summary?.quoteRatio ?? 0,
		};
	});

	for (const summary of quoteSummary) {
		if (seenCustomerIds.has(summary.customerId)) {
			continue;
		}
		mergedRows.push({
			customerId: summary.customerId,
			customerName:
				summary.customerName?.trim() || `Customer ${summary.customerId}`,
			shipments: 0,
			quotes: summary.quotes,
			quoteRatio: summary.quoteRatio,
			brokerageRevenue: 0,
		});
	}

	return {
		...salesPerformance,
		rows: mergedRows,
	};
};
